import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { getTravelInfo, MODE_EMOJI } from '../../services/travelTimeService';
import { getBriefingMode } from '../../hooks/useMorningBriefing';
import { Button, DashboardCard } from '../../ui';
import '../dashboard.css';

// ── Markdown stripper — removes common markdown syntax from AI-generated text ──
function stripMarkdown(text) {
  if (!text) return text;
  return text
    .replace(/^#{1,6}\s+/gm, '')          // # headings
    .replace(/\*\*(.+?)\*\*/g, '$1')       // **bold**
    .replace(/\*(.+?)\*/g, '$1')           // *italic*
    .replace(/^[-*+]\s+/gm, '')            // - bullet points
    .replace(/^\d+\.\s+/gm, '')            // 1. numbered lists
    .replace(/`{1,3}[^`]*`{1,3}/g, '$&'.replace(/`/g, '')) // `code`
    .replace(/_{1,2}(.+?)_{1,2}/g, '$1')   // __bold__ / _italic_
    .replace(/~~(.+?)~~/g, '$1')           // ~~strikethrough~~
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // [link](url)
    .replace(/\n{3,}/g, '\n\n')            // collapse excess blank lines
    .trim();
}

// Split AI text into summary sentence (first line) and body (remainder)
function parseSummary(text) {
  if (!text) return { summaryLine: null, bodyText: null };
  const idx = text.indexOf('\n');
  if (idx === -1) return { summaryLine: text, bodyText: null };
  return {
    summaryLine: text.slice(0, idx).trim() || null,
    bodyText: text.slice(idx).trim() || null,
  };
}

// ── Block B: the look is dashboard.css on the v2 tokens. Priority uses decision 50's colours.
const PRIORITY_TOKEN = { High: 'var(--priority-high)', Medium: 'var(--priority-medium)', Low: 'var(--priority-low)' };

// The header-bar pastel per time of day (the old left-border stripes are gone: brief 6).
const BAR_COLOR = { morning: 'pastel-yellow', afternoon: 'pastel-blue', evening: 'pastel-periwinkle' };

// ── Priority dot ──────────────────────────────────────────────────────────────
function PriorityDot({ priority }) {
  const k = String(priority || '');
  const key = k.charAt(0).toUpperCase() + k.slice(1).toLowerCase();
  return <span className="dash-brief__pdot" style={{ '--ui-bg': PRIORITY_TOKEN[key] || 'var(--surface-2)' }} aria-hidden="true" />;
}

function OverdueFlag({ days }) {
  return <span className="dash-brief__flag">{days}d overdue</span>;
}

// ── Section block ─────────────────────────────────────────────────────────────
function BriefingSection({ icon, title, children }) {
  return (
    <div className="dash-brief__section">
      <p className="dash-brief__label"><span aria-hidden="true">{icon}&nbsp; </span>{title}</p>
      {children}
    </div>
  );
}

// Email-detected events awaiting confirmation — shown in all three briefings.
// Informational only; the user confirms via the calendar banner or Aidey chat.
function DetectedEventsSection({ eventProposals = [] }) {
  if (!eventProposals.length) return null;
  const fmt = (p) => {
    const d = new Date(p.event_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    return `${p.title}, ${d}${p.event_time ? ` ${p.event_time}` : ''}`;
  };
  return (
    <BriefingSection icon="📨" title="Detected events">
      {eventProposals.slice(0, 5).map((p, i) => {
        const clash = p.clash_result || {};
        const clashes = (clash.checked !== false && Array.isArray(clash.clashes)) ? clash.clashes : [];
        return (
          <div key={p.id || i} className="dash-brief__row">
            <span>
              {fmt(p)}
              {clashes.length > 0 && <span className="dash-brief__flag">clashes</span>}
            </span>
          </div>
        );
      })}
      <span className="dash-brief__sub">Ask Aidey to go through them, or confirm from your calendar.</span>
    </BriefingSection>
  );
}

function EmptyRow({ children }) {
  return <p className="dash-brief__empty">{children}</p>;
}

// ── Shared meeting row ────────────────────────────────────────────────────────
function MeetingRow({ m, i }) {
  const { currentUser } = useCurrentUser();
  const [travelInfo, setTravelInfo] = useState(null);

  useEffect(() => {
    if (!m.location) return;
    const tp = currentUser?.preferences?.travel || {};
    const origin = tp.homeAddress || tp.workplaceAddress;
    if (!origin) return;
    let cancelled = false;
    getTravelInfo(origin, m.location)
      .then(result => { if (!cancelled) setTravelInfo(result); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [m.location, currentUser]);

  const travelPrefs = currentUser?.preferences?.travel || {};
  const mapsOrigin = travelPrefs.homeAddress || travelPrefs.workplaceAddress;
  const fallbackBuffer = travelPrefs.travelBuffer || 60;

  return (
    <div key={i} className="dash-brief__row">
      <span className="dash-brief__time">{m.time}</span>
      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <strong>{m.title}</strong>
        {m.location && (
          <span className="dash-brief__sub">
            {m.location}
            {mapsOrigin && (
              <span>
                {' · '}
                {travelInfo
                  ? `${MODE_EMOJI[travelInfo.recommendedMode] || '🚗'} ~${travelInfo.bufferMins} min`
                  : `~${fallbackBuffer} min est.`}
                {' · '}
                <a
                  href={`https://www.google.com/maps/dir/${encodeURIComponent(mapsOrigin)}/${encodeURIComponent(m.location)}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Maps
                </a>
              </span>
            )}
          </span>
        )}
      </span>
    </div>
  );
}

function InvoiceRows({ invoiceAlerts }) {
  return invoiceAlerts.map((inv, i) => (
    <div key={i} className="dash-brief__row">
      <span>
        <strong>{inv.clientName}</strong>{' '}
        {inv.currency}{Number(inv.amount).toLocaleString()}
        {inv.daysOverdue > 0
          ? <OverdueFlag days={inv.daysOverdue} />
          : <span className="dash-brief__flag dash-brief__flag--soon">due today</span>}
      </span>
    </div>
  ));
}

function DoneRow({ t, i }) {
  return (
    <div key={i} className="dash-brief__row">
      <span className="dash-brief__tick" aria-hidden="true">✓</span>
      <span>{t.title}</span>
    </div>
  );
}

// ── Mode-specific expanded sections ──────────────────────────────────────────
function MorningDetail({ briefing }) {
  const { meetings = [], priorityTasks = [], invoiceAlerts = [], teamFlags = [], eventProposals = [], weather } = briefing;
  return (
    <>
      <BriefingSection icon="📅" title="Today's schedule">
        {meetings.length === 0 ? (
          <EmptyRow>No meetings today</EmptyRow>
        ) : meetings.map((m, i) => <MeetingRow key={i} m={m} i={i} />)}
      </BriefingSection>

      <BriefingSection icon="✅" title="Priority tasks">
        {priorityTasks.length === 0 ? (
          <EmptyRow>You're clear on tasks today</EmptyRow>
        ) : priorityTasks.map((t, i) => (
          <div key={i} className="dash-brief__row">
            <PriorityDot priority={t.priority} />
            <span>
              <strong>{t.title}</strong>
              <span className="dash-brief__sub"> {t.priority}</span>
              {t.daysOverdue > 0 && <OverdueFlag days={t.daysOverdue} />}
            </span>
          </div>
        ))}
        <MoreOverdue shown={priorityTasks.filter(t => t.daysOverdue > 0).length} total={briefing.overdueCount} />
      </BriefingSection>

      {invoiceAlerts.length > 0 && (
        <BriefingSection icon="🧾" title="Invoice alerts">
          <InvoiceRows invoiceAlerts={invoiceAlerts} />
        </BriefingSection>
      )}

      {teamFlags.length > 0 && (
        <BriefingSection icon="🚩" title="Team flags">
          {teamFlags.map((f, i) => (
            <div key={i} className="dash-brief__row" style={{ flexDirection: 'column', gap: 2 }}>
              <span>
                <strong>{f.memberName}</strong>
                {' '}hasn't responded to{' '}
                <strong>"{f.taskName}"</strong>
              </span>
              <span className="dash-brief__sub">Assigned {f.assignedDate} · 48h+ ago</span>
            </div>
          ))}
        </BriefingSection>
      )}

      {weather && (
        <BriefingSection icon="🌤" title="Weather">
          <p className="dash-brief__body">{weather}</p>
        </BriefingSection>
      )}

      <DetectedEventsSection eventProposals={eventProposals} />
    </>
  );
}

// Overdue tasks merged into "still to do" so overdue items always count as outstanding,
// deduplicated by title in case both arrays contain the same task.
function stillToDoOf(briefing) {
  const { outstandingToday = [], overdueFollowUps = [] } = briefing;
  const outstandingTitles = new Set(outstandingToday.map(t => t.title));
  return [
    ...outstandingToday,
    ...overdueFollowUps.filter(t => !outstandingTitles.has(t.title)),
  ];
}

// The briefing lists at most five overdue tasks; the count is the whole of them (the one overdue
// rule, Carla 2026-10-11), so the rest are said rather than silently left off.
function MoreOverdue({ shown, total }) {
  const more = (total || 0) - shown;
  if (more <= 0) return null;
  return <span className="dash-brief__sub">+{more} more overdue task{more === 1 ? '' : 's'}</span>;
}

function AfternoonDetail({ briefing }) {
  const {
    completedToday = [],
    remainingMeetings = [],
    tomorrowMeetings = [],
    eventProposals = [],
  } = briefing;
  const stillToDo = stillToDoOf(briefing);

  return (
    <>
      <BriefingSection icon="✅" title="Completed today">
        {completedToday.length === 0 ? (
          <EmptyRow>Nothing marked complete yet</EmptyRow>
        ) : completedToday.map((t, i) => <DoneRow key={i} t={t} i={i} />)}
      </BriefingSection>

      <BriefingSection icon="⏳" title="Still to do">
        {stillToDo.length === 0 ? (
          <EmptyRow>All clear, nothing outstanding</EmptyRow>
        ) : stillToDo.map((t, i) => (
          <div key={i} className="dash-brief__row">
            <PriorityDot priority={t.priority} />
            <span>
              <strong>{t.title}</strong>
              {t.time && <span className="dash-brief__sub"> {t.time}</span>}
              {t.daysOverdue > 0 && <OverdueFlag days={t.daysOverdue} />}
            </span>
          </div>
        ))}
        <MoreOverdue shown={stillToDo.filter(t => t.daysOverdue > 0).length} total={briefing.overdueCount} />
      </BriefingSection>

      {remainingMeetings.length > 0 && (
        <BriefingSection icon="📅" title="Still ahead today">
          {remainingMeetings.map((m, i) => <MeetingRow key={i} m={m} i={i} />)}
        </BriefingSection>
      )}

      <BriefingSection icon="📆" title="Tomorrow's calendar">
        {tomorrowMeetings.length === 0 ? (
          <EmptyRow>Nothing scheduled yet for tomorrow</EmptyRow>
        ) : tomorrowMeetings.map((m, i) => <MeetingRow key={i} m={m} i={i} />)}
      </BriefingSection>

      <DetectedEventsSection eventProposals={eventProposals} />
    </>
  );
}

function EveningDetail({ briefing }) {
  const {
    completedToday = [],
    rolledOver = [],
    firstTomorrowMeeting,
    tomorrowMeetings = [],
    invoiceAlerts = [],
    eventProposals = [],
  } = briefing;

  return (
    <>
      <BriefingSection icon="🏁" title="What you accomplished">
        {completedToday.length === 0 ? (
          <EmptyRow>No tasks marked complete today</EmptyRow>
        ) : completedToday.map((t, i) => <DoneRow key={i} t={t} i={i} />)}
      </BriefingSection>

      {rolledOver.length > 0 && (
        <BriefingSection icon="↩️" title="Rolled to tomorrow">
          {rolledOver.map((t, i) => (
            <div key={i} className="dash-brief__row">
              <PriorityDot priority={t.priority} />
              <span>{t.title}</span>
            </div>
          ))}
        </BriefingSection>
      )}

      {briefing.overdueCount > 0 && (
        <BriefingSection icon="⏰" title="Overdue">
          <div className="dash-brief__row"><span>{briefing.overdueCount} task{briefing.overdueCount === 1 ? ' is' : 's are'} past due</span></div>
        </BriefingSection>
      )}

      <BriefingSection icon="🌅" title="Tomorrow starts with">
        {!firstTomorrowMeeting ? (
          <EmptyRow>Nothing in the diary yet</EmptyRow>
        ) : (
          <MeetingRow m={firstTomorrowMeeting} i={0} />
        )}
        {tomorrowMeetings.length > 1 && (
          <span className="dash-brief__sub">
            +{tomorrowMeetings.length - 1} more meeting{tomorrowMeetings.length > 2 ? 's' : ''}
          </span>
        )}
      </BriefingSection>

      {invoiceAlerts.length > 0 && (
        <BriefingSection icon="🧾" title="Invoice alerts">
          <InvoiceRows invoiceAlerts={invoiceAlerts} />
        </BriefingSection>
      )}

      <DetectedEventsSection eventProposals={eventProposals} />
    </>
  );
}

// "X of Y done" for the header bar, where the briefing carries both numbers: the afternoon
// (completed against completed + still to do) and the evening (completed against completed +
// rolled over). The morning briefing has no done count, so it shows none.
function doneMeta(mode, briefing) {
  const done = (briefing.completedToday || []).length;
  let open = null;
  if (mode === 'afternoon') open = stillToDoOf(briefing).length;
  else if (mode === 'evening') open = (briefing.rolledOver || []).length;
  if (open === null || done + open === 0) return null;
  return `${done} of ${done + open} done`;
}

// ── Main component ────────────────────────────────────────────────────────────
export default function BriefingCard({
  briefing,
  loading,
  onResendEmail,
  emailConnected,
  mode: modeProp,
  briefingTime,
}) {
  const { t } = useTranslation('common');
  const mode        = modeProp || getBriefingMode();
  const barColor    = BAR_COLOR[mode] || BAR_COLOR.morning;

  const modeConfigI18n = {
    morning:   { icon: '☀️',  title: t('briefing.morning_title'),   greeting: t('briefing.good_morning'),   loading: t('briefing.loading_morning') },
    afternoon: { icon: '🌤',  title: t('briefing.afternoon_title'), greeting: t('briefing.good_afternoon'), loading: t('briefing.loading_afternoon') },
    evening:   { icon: '🌙',  title: t('briefing.evening_title'),   greeting: t('briefing.good_evening'),   loading: t('briefing.loading_evening') },
  };
  const config = modeConfigI18n[mode] || modeConfigI18n.morning;

  const storageKey = `briefing-expanded-${mode}`;
  const [expanded, setExpanded] = useState(() => {
    try { return sessionStorage.getItem(storageKey) === 'true'; } catch { return false; }
  });

  function toggleExpanded() {
    const next = !expanded;
    setExpanded(next);
    try { sessionStorage.setItem(storageKey, String(next)); } catch {}
  }

  if (!briefing) {
    let placeholderText = config.loading;
    if (!loading) {
      if (mode === 'morning' && briefingTime) {
        const [h, m] = briefingTime.split(':').map(Number);
        const now = new Date();
        const isPast = now.getHours() > h || (now.getHours() === h && now.getMinutes() >= m);
        placeholderText = isPast
          ? t('briefing.loading_morning')
          : t('briefing.ready_at', { time: briefingTime });
      } else {
        placeholderText = `Generating your ${config.title.toLowerCase()}…`;
      }
    }
    return (
      <DashboardCard tint={barColor} title={config.title}>
        <p className="dash-lead" role="status"><span aria-hidden="true">{config.icon} </span>{placeholderText}</p>
      </DashboardCard>
    );
  }

  const { greeting } = briefing;
  const rawSummary = stripMarkdown(briefing.aiSummary);
  const { summaryLine, bodyText } = parseSummary(rawSummary);
  const meta = doneMeta(mode, briefing) || greeting?.dateStr || null;
  const moreId = `briefing-more-${mode}`;

  return (
    <DashboardCard tint={barColor} title={config.title} meta={meta}
      actions={(
        <>
          <Button variant="tertiary" size="sm" onClick={toggleExpanded} aria-expanded={expanded} aria-controls={moreId}>
            {expanded ? 'Show less ↑' : 'See more ↓'}
          </Button>
          {/* Resend email */}
          {expanded && emailConnected && (
            <Button variant="tertiary" size="sm" onClick={onResendEmail}>Resend to email</Button>
          )}
        </>
      )}>
      {/* Decision 87: the tile's title is the strip's h2; the greeting is the h3 under it. */}
      <h3 className="dash-greeting">{config.greeting} {greeting?.name}.</h3>

      {/* The summary sentence: always visible. */}
      {summaryLine && <p className="dash-lead">{summaryLine}</p>}

      {/* The expandable content */}
      <div className="dash-brief__more" id={moreId} hidden={!expanded}>
        {bodyText && <p className="dash-brief__body">{bodyText}</p>}

        <div className="dash-brief__sections">
          {mode === 'morning'   && <MorningDetail   briefing={briefing} />}
          {mode === 'afternoon' && <AfternoonDetail briefing={briefing} />}
          {mode === 'evening'   && <EveningDetail   briefing={briefing} />}
        </div>
      </div>

    </DashboardCard>
  );
}
